/* import __COLOCATED_TEMPLATE__ from './sq-catalog-location-picker.hbs.js'; */
var _dec, _class, _descriptor, _descriptor2;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import ModalsSelectionModal from '@dashboard/shared-ui/components/modals/selection-modal';
import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@ember/component';
import { isNone, isPresent } from '@ember/utils';
import { itemSubunitsServiceActiveSubunits } from '@square/dashboard-utilities/utils/item-subunits';
import { subunitsLabel } from '@dashboard/junk-drawer/utils/subunits';

/**
### Overview

This component is a location selector field. It has a subunits label which reads:

  "All Locations"
  "X Locations"

and when clicked, it launches a modal which allows the user to select the desired
locations for the model.

### Basic Usage

In your template:
```hbs
<div class="form-row">
  {{#sq-form-field label="Location"}}
    {{sq-catalog-location-picker catalogModel=model.item
                                 canSetItemsGloballyEnabled=true}}
  {{/sq-form-field}}
</div>

### Overide title
<div class="form-row">
  {{#sq-form-field label="Location"}}
    {{sq-catalog-location-picker catalogModel=model.item
                                 title="Select Locations"
                                 canSetItemsGloballyEnabled=true}}
  {{/sq-form-field}}
</div>
```

### Parameters
@param {BaseCatalogModel} [catalogModel] BaseCatalogModel
@param {Boolean} [canSetItemsGloballyEnabled] Enables/Disables the "Available at all future locations" checkbox
@param {Function} [onLocationChange] Function called before the location of the catalog model is updated
@param {string[]} [disabledSubunitTokens] List of subunit tokens that are not selectable (DEFAULT: [])
*/
let SqCatalogLocationPicker = (_dec = computed('catalogModel.subunits.@each.nickname'), _class = class SqCatalogLocationPicker extends Component {
  constructor(...args) {
    super(...args);
    _initializerDefineProperty(this, "i18n", _descriptor, this);
    _initializerDefineProperty(this, "modal", _descriptor2, this);
    _defineProperty(this, "canSetItemsGloballyEnabled", true);
    _defineProperty(this, "title", null);
    _defineProperty(this, "catalogModel", null);
    _defineProperty(this, "onLocationChange", null);
    _defineProperty(this, "disabledSubunitTokens", []);
  }
  get subunitsLabel() {
    return subunitsLabel(this.catalogModel?.subunits.mapBy('nickname'), itemSubunitsServiceActiveSubunits().length);
  }
  editLocations() {
    const subunits = itemSubunitsServiceActiveSubunits();
    const catalogModel = this.catalogModel;
    const disabledSubunitTokens = this.disabledSubunitTokens;
    const options = subunits.sortBy('nickname').map(subunit => ({
      checked: catalogModel.enabledForUnit(subunit.get('token')),
      value: subunit,
      label: subunit.get('nickname'),
      disabled: disabledSubunitTokens.includes(subunit.get('token'))
    }));
    if (this.canSetItemsGloballyEnabled) {
      options.push({
        checked: catalogModel.get('globallyEnabled'),
        label: this.i18n.t('items.itemLibrary.edit.subunits.futureLocations'),
        value: null
      });
    }
    this.modal.open(ModalsSelectionModal, {
      title: this.title || this.i18n.t('subunit.edit'),
      selectAllLabel: this.i18n.t('subunit.subunits'),
      options
    }).then(savedOptions => {
      if (isPresent(this.onLocationChange) && isPresent(savedOptions)) {
        return this.onLocationChange(savedOptions);
      } else {
        return savedOptions;
      }
    }).then(savedOptions => {
      if (isNone(savedOptions)) {
        return;
      }
      const catalogModel = this.catalogModel;
      const globallyEnabledOption = this.canSetItemsGloballyEnabled ? savedOptions.pop() : null;
      if (globallyEnabledOption) {
        const {
          didChange,
          checked: globallyEnabled
        } = globallyEnabledOption;
        if (didChange) {
          catalogModel.set('globallyEnabled', globallyEnabled);
          // updateGloballyEnabledForRelationships may not be defined on a Catalog model
          catalogModel.updateGloballyEnabledForRelationships?.(globallyEnabled);
        }
      }
      for (const {
        checked,
        value: subunit
      } of savedOptions.filterBy('didChange')) {
        const token = subunit.get('token');
        if (checked) {
          catalogModel.addMerchantSubunit(subunit);
          if (isPresent(catalogModel.addAffectedRelatedObjects)) {
            catalogModel.addAffectedRelatedObjects(token);
          }
        } else {
          if (isPresent(catalogModel.removeAffectedRelatedObjects)) {
            catalogModel.removeAffectedRelatedObjects(token);
          }
          catalogModel.removeMerchantSubunit(token);
        }
      }
    });
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "i18n", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "modal", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "subunitsLabel", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "subunitsLabel"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "editLocations", [action], Object.getOwnPropertyDescriptor(_class.prototype, "editLocations"), _class.prototype), _class);
export { SqCatalogLocationPicker as default };